import React, { useState, useEffect } from 'react';
import { RoleModelReport } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  BarChart3, 
  Users, 
  Shield, 
  Crown, 
  Key,
  RefreshCw,
  Download,
  Calendar,
  FileText,
  Settings,
  Eye,
  EyeOff,
  Mail
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function RoleModelReportComponent() {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Loading role model report...');
      const data = await RoleModelReport.getReport();
      console.log('Report data received:', data);
      setReportData(data);
    } catch (err) {
      console.error('Error loading role model report:', err);
      setError(`Ошибка загрузки отчета: ${err.message || 'Неизвестная ошибка'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionType = (name) => {
    if (name.includes('.')) {
      const [resource, action] = name.split('.');
      return { resource, action };
    }
    return { resource: 'system', action: name };
  };

  if (isLoading) {
    return (
      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Отчет по ролевой модели
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-slate-600">Загрузка отчета...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Отчет по ролевой модели
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!reportData) {
    return (
      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Отчет по ролевой модели
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-slate-600">Нет данных для отображения</div>
            <Button onClick={loadReport} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Попробовать снова
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { summary, users, roles, permissions, api_methods_by_permission, generated_at } = reportData;

  // Проверяем, что все необходимые данные существуют
  if (!summary || !users || !roles || !permissions) {
    return (
      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Отчет по ролевой модели
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              Неполные данные отчета. Попробуйте обновить страницу.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="elegant-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Отчет по ролевой модели
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadReport}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Обновить
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Экспорт
            </Button>
          </div>
        </div>
        <div className="text-sm text-slate-500">
          Сгенерировано: {formatDate(generated_at)}
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{summary.total_users}</div>
                  <div className="text-sm text-slate-600">Пользователей</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Crown className="w-5 h-5 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold">{summary.total_roles}</div>
                  <div className="text-sm text-slate-600">Ролей</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold">{summary.total_permissions}</div>
                  <div className="text-sm text-slate-600">Прав</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Key className="w-5 h-5 text-orange-600" />
                <div>
                  <div className="text-2xl font-bold">{summary.total_api_methods}</div>
                  <div className="text-sm text-slate-600">API методов</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6">
          {[
            { id: 'summary', label: 'Обзор', icon: BarChart3 },
            { id: 'users', label: 'Пользователи', icon: Users },
            { id: 'roles', label: 'Роли', icon: Crown },
            { id: 'permissions', label: 'Права', icon: Shield },
            { id: 'api', label: 'API методы', icon: Key }
          ].map(tab => {
            const IconComponent = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2"
              >
                <IconComponent className="w-4 h-4" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'summary' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Распределение пользователей по ролям</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {roles.map(role => {
                    const usersWithRole = users.filter(user => user.role_name === role.name);
                    return (
                      <div key={role.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Crown className="w-4 h-4 text-purple-600" />
                          <span className="font-medium">{role.name}</span>
                          <Badge variant="outline">{role.permission_count} прав</Badge>
                        </div>
                        <div className="text-sm text-slate-600">
                          {usersWithRole.length} пользователей
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Права по ресурсам</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(api_methods_by_permission).map(([permission, methods]) => (
                    <div key={permission} className="p-3 border rounded-lg">
                      <div className="font-medium text-sm mb-2">{permission}</div>
                      <div className="space-y-1">
                        {methods.slice(0, 3).map(method => (
                          <div key={method} className="text-xs text-slate-600 font-mono">
                            {method}
                          </div>
                        ))}
                        {methods.length > 3 && (
                          <div className="text-xs text-slate-400">
                            +{methods.length - 3} еще методов
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Пользователи и их роли</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Пользователь</TableHead>
                      <TableHead>Роль</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Дата создания</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                              <Users className="w-4 h-4 text-amber-600" />
                            </div>
                            <div>
                              <div className="font-medium">{user.full_name}</div>
                              <div className="text-sm text-slate-500 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            {user.role_name || 'Не назначена'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.is_active ? "default" : "secondary"}>
                            {user.is_active ? (
                              <>
                                <Eye className="w-3 h-3 mr-1" />
                                Активен
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-3 h-3 mr-1" />
                                Неактивен
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {formatDate(user.created_date)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'roles' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Роли и их права</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {roles.map((role) => (
                  <Card key={role.id} className="border-l-4 border-l-purple-500">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Crown className="w-5 h-5 text-purple-600" />
                          <div>
                            <CardTitle className="text-lg">{role.name}</CardTitle>
                            <div className="text-sm text-slate-600">{role.description}</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={role.is_system_role ? "default" : "outline"}>
                            {role.is_system_role ? (
                              <>
                                <Settings className="w-3 h-3 mr-1" />
                                Системная
                              </>
                            ) : (
                              <>
                                <Users className="w-3 h-3 mr-1" />
                                Пользовательская
                              </>
                            )}
                          </Badge>
                          <Badge variant="secondary">
                            {role.permission_count} прав
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {role.permissions.map((permission) => {
                          const { resource, action } = getPermissionType(permission.name);
                          return (
                            <div key={permission.id} className="flex items-center space-x-2 p-2 bg-slate-50 rounded">
                              <Shield className="w-4 h-4 text-green-600" />
                              <div>
                                <div className="font-medium text-sm">{permission.name}</div>
                                <div className="text-xs text-slate-600">{permission.description}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'permissions' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Права доступа</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Право</TableHead>
                      <TableHead>Ресурс</TableHead>
                      <TableHead>Действие</TableHead>
                      <TableHead>Описание</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {permissions.map((permission) => {
                      const { resource, action } = getPermissionType(permission.name);
                      return (
                        <TableRow key={permission.id}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <Shield className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <div className="font-medium font-mono">{permission.name}</div>
                                <div className="text-sm text-slate-500">ID: {permission.id.slice(0, 8)}...</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-slate-50">
                              {resource}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {action}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-xs">
                              {permission.description ? (
                                <div className="flex items-start gap-1">
                                  <FileText className="w-3 h-3 mt-0.5 text-slate-400" />
                                  <span className="text-sm text-slate-600">{permission.description}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">Нет описания</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'api' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API методы и требуемые права</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(api_methods_by_permission).map(([permission, methods]) => (
                  <Card key={permission} className="border-l-4 border-l-orange-500">
                    <CardHeader>
                      <div className="flex items-center space-x-3">
                        <Key className="w-5 h-5 text-orange-600" />
                        <div>
                          <CardTitle className="text-lg">{permission}</CardTitle>
                          <div className="text-sm text-slate-600">
                            {methods.length} метод(ов) API
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {methods.map((method) => (
                          <div key={method} className="p-2 bg-slate-50 rounded font-mono text-sm">
                            {method}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}
